import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { createReservation } from "@/lib/api";

export const ReserveModal = ({ open, shipDate, onClose, onReserved }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: "", email: "" });
    setDone(false);
    setLoading(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.email.includes("@")) {
      toast.error("Name and a valid email are required, operator.");
      return;
    }
    setLoading(true);
    try {
      const res = await createReservation({ name: form.name.trim(), email: form.email.trim() });
      setDone(true);
      onReserved?.(res);
      toast.success(res.already ? "You're already on the list." : "Deck reserved. Welcome to the frontline.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't lock your reservation. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-sf-bg border border-sf-border rounded-sm px-4 py-3 text-sf-bone placeholder:text-sf-muted font-body text-sm focus:outline-none focus:border-sf-ember transition-colors";
  const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-sf-dim mb-1.5 block";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="reserve-modal"
        >
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={loading ? undefined : close} />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-md bg-sf-surface border border-sf-border rounded-md p-7 md:p-8"
          >
            <button
              onClick={close}
              disabled={loading}
              data-testid="reserve-close-button"
              className="absolute top-5 right-5 text-sf-dim hover:text-sf-bone transition-colors disabled:opacity-40"
              aria-label="Close"
            >
              <X size={22} />
            </button>

            {done ? (
              <div className="text-center py-4" data-testid="reserve-success">
                <div className="flex justify-center mb-5">
                  <CheckCircle2 className="text-sf-ember" size={46} />
                </div>
                <h3 className="sf-stencil text-2xl text-sf-bone tracking-wide">Position Secured</h3>
                <p className="mt-3 text-sm text-sf-dim leading-relaxed">
                  {"Your deck is reserved — no payment yet. Once we reach our first-wave goal, we'll email you to complete your order. First-wave ships "}{shipDate}.
                </p>
                <button
                  onClick={close}
                  data-testid="reserve-done-button"
                  className="mt-7 bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-stencil uppercase tracking-widest text-sm px-6 py-3 rounded-sm"
                >
                  Continue
                </button>
              </div>
            ) : (
              <>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sf-ember">
                  Reserve · Standard Issue Deck
                </div>
                <h3 className="mt-2 sf-stencil text-3xl text-sf-bone tracking-wide leading-none">
                  Claim your spot
                </h3>
                <p className="mt-3 text-sm text-sf-dim leading-relaxed">
                  Reserve free — no payment now. We only collect payment once we hit our first-wave goal
                  and production begins. Ships {shipDate}.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label className={labelCls} htmlFor="rv-name">Full Name</label>
                    <input id="rv-name" data-testid="reserve-name-input" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="rv-email">Email</label>
                    <input id="rv-email" type="email" data-testid="reserve-email-input" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="reserve-submit-button"
                    className="w-full mt-2 bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-stencil uppercase tracking-[0.15em] text-base px-8 py-4 rounded-sm hover:shadow-[0_0_28px_rgba(210,84,51,0.5)] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <><Loader2 className="animate-spin" size={18} /> Reserving…</>
                    ) : (
                      <>Reserve My Deck — Free</>
                    )}
                  </button>
                  <p className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sf-muted">
                    <ShieldCheck size={13} className="text-sf-steel" /> No card required · No charge today
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReserveModal;
